import {
  AuthZClient,
  CareerClient,
  CareerServiceConfigClient,
  CareerServiceConfiguration,
  EntityType,
  IBookmark,
  UserPreferences,
} from '../models/Career.types';
import { IConfig } from '../models/IConfig';

/**
 * Manages user's saved bookmarks and CDP.
 */
export class CareerService {
  authZClient: AuthZClient;
  careerClient: CareerClient;
  careerServiceConfigClient: CareerServiceConfigClient;

  public constructor(config: IConfig, baseUrl: string) {
    this.authZClient = new AuthZClient(config, baseUrl);
    this.careerClient = new CareerClient(config, baseUrl);
    this.careerServiceConfigClient = new CareerServiceConfigClient(config, baseUrl);
  }

  /**
   * Checks user's permissions.
   */
  public async checkAccess(): Promise<any> {
    if (__IS_E2E_TESTING__) return Promise.resolve({ isCdpTabVisible: true });
    return await this.authZClient.checkAccess();
  }

  // region bookmarks
  /**
   * Returns the user's current bookmarks.
   * @throws whatever error the service throws
   */
  public async getBookmarks(): Promise<IBookmark[]> {
    const serviceResponse = await this.careerClient.list();
    return serviceResponse.map(this.mapToBookmark);
  }

  /**
   * Adds the given ID to bookmarks, returning the ID of the new bookmark.
   * @throws whatever error the service throws
   */
  public async addBookmark(id: string): Promise<string> {
    await this.careerClient.post(this.mapToBookmarkPayload(id, true));
    return id;
  }

  /**
   * Removes the given ID from bookmarks, returning the ID of the removed bookmark
   * @throws whatever error the service throws
   */
  public async removeBookmark(id: string): Promise<string> {
    await this.careerClient.post(this.mapToBookmarkPayload(id, false));
    return id;
  }

  /** Converts service response to simpler client-side model */
  private mapToBookmark(serviceResponse: UserPreferences): IBookmark {
    return {
      id: serviceResponse.entityId,
      type: serviceResponse.entityType,
      isActive: serviceResponse.isActive,
    };
  }

  /**
   * Maps ID to a role bookmark payload (only role bookmarking supported for now)
   * @param id the ID of the role to bookmark
   * @param isActive whether the bookmark should become active
   */
  private mapToBookmarkPayload(id: string, isActive: boolean): UserPreferences {
    return {
      id: '', // NOT USED, but defined in class
      userId: '', // NOT USED, but defined in class
      entityId: id,
      entityType: EntityType.Role,
      entityName: id, // we don't care about name, only ID for lookup
      isActive: isActive,
      versionInfo: {
        createdTimestamp: new Date(),
        createdBy: '',
        modifiedTimestamp: new Date(),
        modifiedBy: '',
      },
    };
  }
  // endregion bookmarks

  public async getCareerServiceConfiguration(): Promise<CareerServiceConfiguration> {
    return await this.careerServiceConfigClient.getCareerServiceConfiguration();
  }
}
